import { Rider } from '../api/rest-client';

/**
 * True when the rider is a coach. Mirrors the coaches-last ordering used by
 * the Dashboard Rider selector.
 */
function isCoach(rider: Rider): boolean {
  return rider.type === 'Coach';
}

/**
 * Return the members of a group, sorted for display.
 *
 * Pure function (no React/DOM/network) — exported for property-based testing.
 *
 * Selection semantics (`groupName`):
 * - `''` (nothing selected)      -> returns [] (no roster shown)
 * - any other value (group name) -> riders where `rider.group === groupName`
 *
 * Sort order matches the Dashboard Rider selector:
 * - non-coaches before coaches (coaches last)
 * - then case-insensitive alphabetical by `name`
 *
 * Does not mutate the input array.
 *
 * Requirements: FR-3, FR-4; Business rules BR-2, BR-3, BR-6, BR-7.
 * Properties: P-1 (subset/completeness), P-2 (size), P-3 (sort order),
 * P-5 (no mutation / empty selection).
 */
export function membersOf(riders: Rider[], groupName: string): Rider[] {
  if (!groupName) {
    return [];
  }

  return riders
    .filter((rider) => rider.group === groupName)
    .slice()
    .sort((a, b) => {
      const aIsCoach = isCoach(a) ? 1 : 0;
      const bIsCoach = isCoach(b) ? 1 : 0;
      if (aIsCoach !== bIsCoach) return aIsCoach - bIsCoach;
      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    });
}
